import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, type Identifier } from "ra-core";

import {
  DashboardRequestError,
  dashboardRequest,
} from "../providers/aws/client";
import {
  parseFramingSceneInspector,
  type FramingSceneInspector,
} from "./framingInspector";

export type FramingSceneInspectorRequest = {
  sessionId: Identifier;
  sceneId: string;
  attemptId?: string | null;
};

export async function fetchFramingSceneInspector(
  { sessionId, sceneId, attemptId }: FramingSceneInspectorRequest,
  signal?: AbortSignal,
): Promise<FramingSceneInspector> {
  const query = new URLSearchParams();
  if (attemptId) query.set("attempt", attemptId);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await dashboardRequest<unknown>(
    `/video-sessions/${encodeURIComponent(String(sessionId))}/framing-scenes/${encodeURIComponent(sceneId)}${suffix}`,
    signal ? { signal } : {},
  );
  return parseFramingSceneInspector(response);
}

function shouldRetry(failureCount: number, error: Error): boolean {
  if (
    error instanceof DashboardRequestError &&
    [401, 403, 404].includes(error.status)
  ) {
    return false;
  }
  return failureCount < 2;
}

export function useFramingSceneInspector({
  enabled,
  ...request
}: FramingSceneInspectorRequest & { enabled: boolean }) {
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: [
      "framing-scene-inspector",
      String(request.sessionId),
      request.sceneId,
      request.attemptId ?? "current",
      identity?.id == null ? "no-identity" : String(identity.id),
    ],
    // React Query's signal aborts superseded fetches so a stale response can
    // never replace a newer scene/attempt selection (CROP-WB-006).
    queryFn: ({ signal }) => fetchFramingSceneInspector(request, signal),
    enabled: enabled && identity?.id != null,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: shouldRetry,
  });
}
